import { CategoryId } from '$/epg/categories'
import { epgChannelsModel, LOAD_MORE_CHANNELS_LIMIT } from '$/epg/channels'
import { attach, createEvent, sample, split } from 'effector'
import { model as session } from '~/entities/session'

// load more channels
export const load = createEvent<{
  categoryId: number | CategoryId
  regionId?: number
}>()

const loadMoreAll = createEvent<{
  categoryId: number | CategoryId
  regionId?: number
}>()
const loadMoreByCategory = createEvent<{
  categoryId: number | CategoryId
  regionId?: number
}>()
const loadMoreFavorites = createEvent<{ categoryId: number | CategoryId }>()

// load more channels by category All id effect
export const loadMoreAllFx = attach({
  mapParams: ({
    id,
    offset,
    regionId,
  }: {
    id: number | CategoryId
    offset: number
    regionId?: number
  }) => ({
    byCategoryId: id,
    id,
    offset,
    limit: LOAD_MORE_CHANNELS_LIMIT,
    regionId,
  }),
  effect: epgChannelsModel.loadAllPageFx,
})

// load more channels by regular category id effect
export const loadMoreByCategoryFx = attach({
  mapParams: ({
    id,
    offset,
    regionId,
  }: {
    id: number | CategoryId
    offset: number
    regionId?: number
  }) => ({
    byCategoryId: id,
    id,
    offset,
    limit: LOAD_MORE_CHANNELS_LIMIT,
    regionId,
  }),
  effect: epgChannelsModel.loadByCategoryPageFx,
})

// load more favorite channels by Favorite category id effect
export const loadMoreFavoritesFx = attach({
  mapParams: ({ id, offset }: { id: number | CategoryId; offset: number }) => ({
    byCategoryId: id,
    id,
    offset,
    limit: LOAD_MORE_CHANNELS_LIMIT,
  }),
  effect: epgChannelsModel.loadFavoritesPageFx,
})

// Split load event into three events so that three different requests can be made later
// (requests to load all channels, by category id and favorites)
split({
  source: load,
  match: {
    loadMoreAll: ({ categoryId }) => categoryId === CategoryId.All,
    loadMoreFavorites: ({ categoryId }) => categoryId === CategoryId.Favorite,
  },
  cases: {
    loadMoreAll,
    loadMoreFavorites,
    __: loadMoreByCategory,
  },
})

// load more channels
// it takes currently loaded channels for category All count, calculates offset to load and loads next page
sample({
  clock: loadMoreAll,
  source: {
    loaded: epgChannelsModel.$byCategory,
    pending: epgChannelsModel.loadAllPageFx.pending,
  },
  filter: ({ loaded: [_, meta], pending }, { categoryId }) =>
    pending ? false : Boolean(meta.get(categoryId)?.hasMore),
  fn: ({ loaded: [channels] }, { categoryId, regionId }) => {
    const loaded = channels.get(categoryId) ?? []

    return {
      id: categoryId,
      offset: loaded.length,
      regionId,
    }
  },
  target: loadMoreAllFx,
})

// load more channels
// it takes currently loaded channels for regular category count, calculates offset to load and loads next page
sample({
  clock: loadMoreByCategory,
  source: {
    loaded: epgChannelsModel.$byCategory,
    pending: epgChannelsModel.loadByCategoryPageFx.pending,
  },
  filter: ({ loaded: [_, meta], pending }, { categoryId }) =>
    pending ? false : Boolean(meta.get(categoryId)?.hasMore),
  fn: ({ loaded: [channels] }, { categoryId, regionId }) => {
    const loaded = channels.get(categoryId) ?? []
    return {
      id: categoryId,
      offset: loaded.length,
      regionId,
    }
  },
  target: loadMoreByCategoryFx,
})

// load more channels
// it takes currently loaded Favorites channels count, calculates offset to load and loads next page
sample({
  clock: loadMoreFavorites,
  source: {
    loaded: epgChannelsModel.$byCategory,
    pending: epgChannelsModel.loadFavoritesPageFx.pending,
    isAuthenticated: session.$isAuthenticated,
  },
  filter: ({ loaded: [_, meta], pending, isAuthenticated }, { categoryId }) =>
    pending
      ? false
      : Boolean(meta.get(categoryId)?.hasMore) && Boolean(isAuthenticated),
  fn: ({ loaded: [channels] }, { categoryId }) => {
    const loaded = channels.get(categoryId) ?? []

    return {
      id: categoryId,
      offset: loaded.length,
    }
  },
  target: loadMoreFavoritesFx,
})
